import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_gen-z-jewels-1/artifacts/8dp6l3ul_nova.png";

/* ────────────────────────────────────────── OPTION A: Dark & Luxe ── */
const OptionA = () => (
  <div className="min-h-screen bg-black text-white">
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img src="https://images.unsplash.com/photo-1769305171851-897e01aa095c?w=1920&q=80" alt="Hero" className="w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black" />
      </div>
      <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <div className="flex justify-center mb-8">
            <div className="h-20 overflow-hidden"><img src={LOGO_URL} alt="NOVA" className="h-56 w-auto max-w-none brightness-0 invert" /></div>
          </div>
          <p className="text-xs uppercase tracking-[0.3em] font-medium text-[#C27054] mb-6">Modern Jewelry for Gen Z</p>
          <h1 className="text-5xl lg:text-7xl font-serif tracking-tight leading-none mb-6">
            Redefine Your<br /><span className="italic text-[#C27054]">Style</span>
          </h1>
          <p className="text-base text-gray-300 leading-relaxed mb-8 max-w-2xl mx-auto">
            Break free from traditional jewelry. Bold, minimal, unforgettable gold & silver pieces.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/products" className="bg-[#C27054] text-white rounded-full px-10 py-4 hover:bg-[#A85B42] transition-all duration-300 text-sm uppercase tracking-widest font-semibold">Shop Now</Link>
            <Link to="/virtual-tryon" className="border border-white/40 text-white rounded-full px-10 py-4 hover:bg-white hover:text-black transition-all duration-300 text-sm uppercase tracking-widest font-semibold">Virtual Try-On</Link>
          </div>
        </motion.div>
      </div>
    </section>

    <section className="py-24 px-6 bg-[#0a0a0a]">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
        <Link to="/products?category=gold" className="relative aspect-[4/5] overflow-hidden rounded-2xl group">
          <img src="https://images.pexels.com/photos/3266700/pexels-photo-3266700.jpeg?auto=compress&w=800" alt="Gold" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end p-8">
            <div><h3 className="text-2xl font-serif mb-2">Gold Collection</h3><p className="text-sm text-gray-400 flex items-center gap-2">Explore <ArrowRight size={16} /></p></div>
          </div>
        </Link>
        <Link to="/products?category=silver" className="relative aspect-[4/5] overflow-hidden rounded-2xl group">
          <img src="https://images.unsplash.com/photo-1605884878538-6468614df578?w=800" alt="Silver" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end p-8">
            <div><h3 className="text-2xl font-serif mb-2">Silver Collection</h3><p className="text-sm text-gray-400 flex items-center gap-2">Explore <ArrowRight size={16} /></p></div>
          </div>
        </Link>
      </div>
    </section>
  </div>
);

/* ────────────────────────────────────────── OPTION B: Warm Earth Tones ── */
const OptionB = () => (
  <div className="min-h-screen bg-[#F5E6D3]">
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img src="https://images.pexels.com/photos/34113315/pexels-photo-34113315.jpeg?auto=compress&w=1920" alt="Hero" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#8B4513]/80 via-[#8B4513]/40 to-transparent" />
      </div>
      <div className="relative z-10 text-left px-6 max-w-7xl mx-auto w-full">
        <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} className="max-w-xl">
          <div className="h-16 overflow-hidden mb-6"><img src={LOGO_URL} alt="NOVA" className="h-44 w-auto max-w-none brightness-0 invert" /></div>
          <p className="text-xs uppercase tracking-[0.3em] font-medium text-[#F5DEB3] mb-4">Golden Hour Jewelry</p>
          <h1 className="text-5xl lg:text-7xl font-serif tracking-tight text-white leading-none mb-6">
            Wear the<br /><span className="italic text-[#F5DEB3]">Warmth</span>
          </h1>
          <p className="text-base text-[#F5DEB3]/90 leading-relaxed mb-8">
            Boho-luxury gold & silver for the free-spirited soul. Instagram-worthy, always.
          </p>
          <div className="flex gap-4">
            <Link to="/products" className="bg-[#C27054] text-white rounded-full px-10 py-4 hover:bg-[#A85B42] transition-all duration-300 text-sm uppercase tracking-widest font-semibold">Shop Now</Link>
            <Link to="/virtual-tryon" className="border border-[#F5DEB3] text-[#F5DEB3] rounded-full px-10 py-4 hover:bg-[#F5DEB3] hover:text-[#8B4513] transition-all duration-300 text-sm uppercase tracking-widest font-semibold">Try On</Link>
          </div>
        </motion.div>
      </div>
    </section>

    <section className="py-24 px-6 bg-[#F5E6D3]">
      <div className="max-w-7xl mx-auto text-center mb-16">
        <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#8B4513]/60 mb-4">Collections</p>
        <h2 className="text-4xl lg:text-5xl font-serif tracking-tight text-[#5C3317]">Crafted with Love</h2>
      </div>
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
        <Link to="/products?category=gold" className="relative aspect-[4/5] overflow-hidden rounded-2xl group">
          <img src="https://images.unsplash.com/photo-1730320915228-f70e3c43aa7d?w=800&q=80" alt="Gold" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#5C3317]/70 to-transparent flex items-end p-8">
            <div><h3 className="text-2xl font-serif text-white mb-2">Gold Collection</h3><p className="text-sm text-[#F5DEB3] flex items-center gap-2">Explore <ArrowRight size={16} /></p></div>
          </div>
        </Link>
        <Link to="/products?category=silver" className="relative aspect-[4/5] overflow-hidden rounded-2xl group">
          <img src="https://images.unsplash.com/photo-1605884878538-6468614df578?w=800" alt="Silver" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#5C3317]/70 to-transparent flex items-end p-8">
            <div><h3 className="text-2xl font-serif text-white mb-2">Silver Collection</h3><p className="text-sm text-[#F5DEB3] flex items-center gap-2">Explore <ArrowRight size={16} /></p></div>
          </div>
        </Link>
      </div>
    </section>
  </div>
);

/* ────────────────────────────────────────── OPTION C: Sage & Gold Minimal ── */
const OptionC = () => (
  <div className="min-h-screen bg-[#E4E8DF]">
    <section className="min-h-screen flex items-center px-6">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center w-full">
        <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
          <div className="h-16 overflow-hidden mb-8"><img src={LOGO_URL} alt="NOVA" className="h-44 w-auto max-w-none" /></div>
          <h1 className="text-5xl lg:text-7xl font-serif tracking-tight text-[#2D3B26] leading-none mb-6">
            Less is<br /><span className="italic text-[#C27054]">More</span>
          </h1>
          <p className="text-lg text-[#475740] leading-relaxed mb-8 max-w-lg">
            Minimal jewelry that speaks volumes. Clean lines, pure metals, timeless design for the modern generation.
          </p>
          <div className="flex gap-4">
            <Link to="/products" className="bg-[#C27054] text-white rounded-full px-10 py-4 hover:bg-[#A85B42] transition-all duration-300 text-sm uppercase tracking-widest font-semibold shadow-[0_4px_14px_0_rgba(194,112,84,0.39)]">Shop Now</Link>
            <Link to="/virtual-tryon" className="border border-[#2D3B26] text-[#2D3B26] rounded-full px-10 py-4 hover:bg-[#2D3B26] hover:text-[#E4E8DF] transition-all duration-300 text-sm uppercase tracking-widest font-semibold">Try On</Link>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} className="relative aspect-[3/4] rounded-3xl overflow-hidden">
          <img src="https://images.unsplash.com/photo-1598218940656-7126545fd283?w=800&q=80" alt="Jewelry" className="w-full h-full object-cover" />
        </motion.div>
      </div>
    </section>

    <section className="py-24 px-6 bg-[#FDFBF7]">
      <div className="max-w-7xl mx-auto text-center mb-16">
        <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#7A8A73] mb-4">Collections</p>
        <h2 className="text-4xl lg:text-5xl font-serif tracking-tight text-[#2D3B26]">Pure & Simple</h2>
      </div>
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
        <Link to="/products?category=gold" className="relative aspect-[4/5] overflow-hidden rounded-2xl group bg-[#E4E8DF]">
          <img src="https://images.unsplash.com/photo-1765064067555-3a9e7b4d338c?w=800" alt="Gold" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#2D3B26]/60 to-transparent flex items-end p-8">
            <div><h3 className="text-2xl font-serif text-[#FDFBF7] mb-2">Gold</h3><p className="text-sm text-[#E4E8DF] flex items-center gap-2">Explore <ArrowRight size={16} /></p></div>
          </div>
        </Link>
        <Link to="/products?category=silver" className="relative aspect-[4/5] overflow-hidden rounded-2xl group bg-[#E4E8DF]">
          <img src="https://images.unsplash.com/photo-1605884878538-6468614df578?w=800" alt="Silver" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#2D3B26]/60 to-transparent flex items-end p-8">
            <div><h3 className="text-2xl font-serif text-[#FDFBF7] mb-2">Silver</h3><p className="text-sm text-[#E4E8DF] flex items-center gap-2">Explore <ArrowRight size={16} /></p></div>
          </div>
        </Link>
      </div>
    </section>
  </div>
);

/* ────────────────────────────────────────── OPTION D: Black & Gold Royal ── */
const OptionD = () => (
  <div className="min-h-screen bg-black text-white">
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 bg-black">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(194,112,84,0.15)_0%,_transparent_70%)]" />
      </div>
      <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <div className="flex justify-center mb-10">
            <div className="h-24 overflow-hidden"><img src={LOGO_URL} alt="NOVA" className="h-64 w-auto max-w-none brightness-0 invert" style={{ filter: 'brightness(0) invert(1) sepia(1) saturate(5) hue-rotate(10deg) brightness(1.1)' }} /></div>
          </div>
          <h1 className="text-5xl lg:text-8xl font-serif tracking-tight leading-none mb-6">
            <span style={{ color: '#D4AF37' }}>Born to</span><br /><span className="italic" style={{ color: '#C27054' }}>Shine</span>
          </h1>
          <p className="text-base text-gray-400 leading-relaxed mb-10 max-w-xl mx-auto">
            Gold & silver jewelry that makes a statement. Premium, bold, unapologetically you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/products" className="text-black rounded-full px-10 py-4 transition-all duration-300 text-sm uppercase tracking-widest font-semibold" style={{ background: 'linear-gradient(135deg, #D4AF37, #C27054)' }}>Shop Now</Link>
            <Link to="/virtual-tryon" className="border border-[#D4AF37] text-[#D4AF37] rounded-full px-10 py-4 hover:bg-[#D4AF37] hover:text-black transition-all duration-300 text-sm uppercase tracking-widest font-semibold">Virtual Try-On</Link>
          </div>
        </motion.div>
      </div>
    </section>

    <section className="py-24 px-6 bg-[#050505]">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
        <Link to="/products?category=gold" className="relative aspect-[4/5] overflow-hidden rounded-2xl group border border-[#D4AF37]/20">
          <img src="https://images.unsplash.com/photo-1639660680736-14b79f169a27?w=800&q=80" alt="Gold" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent flex items-end p-8">
            <div><h3 className="text-2xl font-serif mb-2" style={{ color: '#D4AF37' }}>Gold Collection</h3><p className="text-sm text-gray-500 flex items-center gap-2">Explore <ArrowRight size={16} /></p></div>
          </div>
        </Link>
        <Link to="/products?category=silver" className="relative aspect-[4/5] overflow-hidden rounded-2xl group border border-gray-700">
          <img src="https://images.unsplash.com/photo-1605884878538-6468614df578?w=800" alt="Silver" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent flex items-end p-8">
            <div><h3 className="text-2xl font-serif mb-2 text-gray-300">Silver Collection</h3><p className="text-sm text-gray-500 flex items-center gap-2">Explore <ArrowRight size={16} /></p></div>
          </div>
        </Link>
      </div>
    </section>
  </div>
);

/* ────────────────────────────────────────── OPTION E: Gradient Modern ── */
const OptionE = () => (
  <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #E4E8DF 0%, #FDFBF7 50%, #E4E8DF 100%)' }}>
    <section className="min-h-screen flex items-center justify-center px-6">
      <div className="max-w-6xl mx-auto text-center">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <div className="flex justify-center mb-8">
            <div className="h-20 overflow-hidden"><img src={LOGO_URL} alt="NOVA" className="h-56 w-auto max-w-none" /></div>
          </div>
          <p className="text-xs uppercase tracking-[0.3em] font-medium text-[#C27054] mb-6">Modern Jewelry</p>
          <h1 className="text-5xl lg:text-7xl font-serif tracking-tight text-[#2D3B26] leading-none mb-6">
            Glow<br /><span className="italic text-[#C27054]">Different</span>
          </h1>
          <p className="text-lg text-[#475740] leading-relaxed mb-10 max-w-2xl mx-auto">
            Fresh, sustainable, and designed for the new generation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/products" className="bg-[#C27054] text-white rounded-full px-10 py-4 hover:bg-[#A85B42] transition-all duration-300 text-sm uppercase tracking-widest font-semibold shadow-[0_4px_14px_0_rgba(194,112,84,0.39)]">Shop Now</Link>
            <Link to="/virtual-tryon" className="bg-white/60 backdrop-blur-xl border border-[#2D3B26]/10 text-[#2D3B26] rounded-full px-10 py-4 hover:bg-white transition-all duration-300 text-sm uppercase tracking-widest font-semibold shadow-lg">Virtual Try-On</Link>
          </div>
        </motion.div>

        {/* Glass morphism featured cards */}
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3 }} className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white/50 backdrop-blur-xl rounded-3xl p-6 border border-white/60 shadow-xl">
            <img src="https://images.unsplash.com/photo-1765064067555-3a9e7b4d338c?w=400&q=80" alt="Gold" className="w-full aspect-square object-cover rounded-2xl mb-4" />
            <h3 className="text-lg font-serif text-[#2D3B26]">Gold Pieces</h3>
            <p className="text-xs text-[#7A8A73] mt-1">From &#8377;999</p>
          </div>
          <div className="bg-white/50 backdrop-blur-xl rounded-3xl p-6 border border-white/60 shadow-xl">
            <img src="https://images.unsplash.com/photo-1605884878538-6468614df578?w=400" alt="Silver" className="w-full aspect-square object-cover rounded-2xl mb-4" />
            <h3 className="text-lg font-serif text-[#2D3B26]">Silver Pieces</h3>
            <p className="text-xs text-[#7A8A73] mt-1">From &#8377;599</p>
          </div>
          <div className="bg-white/50 backdrop-blur-xl rounded-3xl p-6 border border-white/60 shadow-xl">
            <img src="https://images.unsplash.com/photo-1743264385411-57c883bdc0ea?w=400" alt="Sets" className="w-full aspect-square object-cover rounded-2xl mb-4" />
            <h3 className="text-lg font-serif text-[#2D3B26]">Jewelry Sets</h3>
            <p className="text-xs text-[#7A8A73] mt-1">From &#8377;2499</p>
          </div>
        </motion.div>
      </div>
    </section>
  </div>
);

/* ────────────────────────────────────────── OPTION F: Cherry Red & Gold ── */
const OptionF = () => (
  <div className="min-h-screen bg-[#1a0a0a] text-white">
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img src="https://images.unsplash.com/photo-1594841764811-7113bef41cea?w=1920&q=80" alt="Hero" className="w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a0a0a]/80 via-[#1a0a0a]/50 to-[#1a0a0a]" />
      </div>
      <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <div className="flex justify-center mb-10">
            <div className="h-20 overflow-hidden"><img src={LOGO_URL} alt="NOVA" className="h-56 w-auto max-w-none brightness-0 invert" /></div>
          </div>
          <p className="text-xs uppercase tracking-[0.3em] font-medium text-[#DC143C] mb-6">Luxury Redefined</p>
          <h1 className="text-5xl lg:text-8xl font-serif tracking-tight leading-none mb-6">
            Desire in<br /><span className="italic" style={{ color: '#DC143C' }}>Every Detail</span>
          </h1>
          <p className="text-base text-gray-400 leading-relaxed mb-10 max-w-xl mx-auto">
            Bold gold & silver jewelry that ignites passion. Made for the fearless, the modern, the unforgettable you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/products" className="bg-[#DC143C] text-white rounded-full px-10 py-4 hover:bg-[#B91030] transition-all duration-300 text-sm uppercase tracking-widest font-semibold shadow-[0_4px_20px_0_rgba(220,20,60,0.4)]">Shop Now</Link>
            <Link to="/virtual-tryon" className="border border-[#DC143C]/50 text-[#DC143C] rounded-full px-10 py-4 hover:bg-[#DC143C] hover:text-white transition-all duration-300 text-sm uppercase tracking-widest font-semibold">Virtual Try-On</Link>
          </div>
        </motion.div>
      </div>
    </section>

    <section className="py-24 px-6 bg-[#0f0505]">
      <div className="max-w-7xl mx-auto text-center mb-16">
        <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#DC143C] mb-4">Collections</p>
        <h2 className="text-4xl lg:text-5xl font-serif tracking-tight text-white">Crafted with Fire</h2>
      </div>
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
        <Link to="/products?category=gold" className="relative aspect-[4/5] overflow-hidden rounded-2xl group border border-[#DC143C]/20">
          <img src="https://images.unsplash.com/photo-1763256614634-7feb3ff79ff3?w=800&q=80" alt="Gold" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1a0a0a]/90 to-transparent flex items-end p-8">
            <div><h3 className="text-2xl font-serif text-[#DC143C] mb-2">Gold Collection</h3><p className="text-sm text-gray-500 flex items-center gap-2">Explore <ArrowRight size={16} /></p></div>
          </div>
        </Link>
        <Link to="/products?category=silver" className="relative aspect-[4/5] overflow-hidden rounded-2xl group border border-gray-800">
          <img src="https://images.unsplash.com/photo-1605884878538-6468614df578?w=800" alt="Silver" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1a0a0a]/90 to-transparent flex items-end p-8">
            <div><h3 className="text-2xl font-serif text-gray-300 mb-2">Silver Collection</h3><p className="text-sm text-gray-500 flex items-center gap-2">Explore <ArrowRight size={16} /></p></div>
          </div>
        </Link>
      </div>
    </section>

    <section className="py-24 px-6 bg-[#1a0a0a]">
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
        <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
          <div className="flex items-center gap-2 mb-4">
            <Sparkles size={20} className="text-[#DC143C]" />
            <p className="text-xs uppercase tracking-[0.2em] font-medium text-gray-500">AI Powered</p>
          </div>
          <h2 className="text-4xl lg:text-5xl font-serif tracking-tight text-white leading-[1.1] mb-6">
            Try Before<br /><span className="italic text-[#DC143C]">You Buy</span>
          </h2>
          <p className="text-base text-gray-400 leading-relaxed mb-8">
            Our AI virtual try-on lets you see every piece on you, from anywhere. No guesswork.
          </p>
          <Link to="/virtual-tryon" className="inline-block bg-[#DC143C] text-white rounded-full px-8 py-4 hover:bg-[#B91030] transition-all duration-300 shadow-[0_4px_20px_0_rgba(220,20,60,0.4)] text-sm uppercase tracking-widest font-semibold">Try Now</Link>
        </motion.div>
        <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }} className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-[#DC143C]/20">
          <img src="https://images.unsplash.com/photo-1743264385411-57c883bdc0ea?w=800" alt="Virtual Try-On" className="w-full h-full object-cover" />
        </motion.div>
      </div>
    </section>

    <section className="py-24 px-6 bg-[#0f0505]">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-12">
        {[
          { title: 'Premium Quality', desc: 'Certified gold and silver crafted with precision.' },
          { title: 'Modern Design', desc: 'Contemporary pieces that break the mold.' },
          { title: 'For Gen Z', desc: 'Designed for those who value authenticity.' },
        ].map((item, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: i * 0.2 }} viewport={{ once: true }} className="text-center">
            <div className="w-16 h-16 bg-[#DC143C]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Sparkles size={24} className="text-[#DC143C]" />
            </div>
            <h3 className="text-xl font-serif text-white mb-3">{item.title}</h3>
            <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
          </motion.div>
        ))}
      </div>
    </section>
  </div>
);

/* ────────────────────────────────────────── SELECTOR ── */
const HomePreview = () => {
  const [searchParams] = useSearchParams();
  const option = searchParams.get('option') || 'a';

  switch (option) {
    case 'a': return <OptionA />;
    case 'b': return <OptionB />;
    case 'c': return <OptionC />;
    case 'd': return <OptionD />;
    case 'e': return <OptionE />;
    case 'f': return <OptionF />;
    default: return <OptionA />;
  }
};

export default HomePreview;
